import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

export type CookieConsentStatus = 'pending' | 'accepted';

const STATUS_KEY = 'cookie-consent-status';

const CookieConsentContext = createContext<{
  status: CookieConsentStatus;
  hasConsented: boolean;
  acceptAll: () => void;
  resetConsent: () => void;
}>({
  status: 'pending',
  hasConsented: false,
  acceptAll: () => {},
  resetConsent: () => {},
});

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};

export const CookieConsentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [status, setStatus] = useState<CookieConsentStatus>('pending');
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    try {
      const storedStatus = localStorage.getItem(
        STATUS_KEY,
      ) as CookieConsentStatus | null;

      if (storedStatus === 'accepted') {
        setStatus('accepted');
        setHasConsented(true);
      }
    } catch (error) {
      console.error('Error loading cookie consent status:', error);
    }
  }, []);

  const acceptAll = useCallback(() => {
    try {
      localStorage.setItem(STATUS_KEY, 'accepted');
      setStatus('accepted');
      setHasConsented(true);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }, []);

  const resetConsent = useCallback(() => {
    try {
      localStorage.removeItem(STATUS_KEY);
      setStatus('pending');
      setHasConsented(false);
    } catch (error) {
      console.error('Error resetting cookie consent:', error);
    }
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        status,
        hasConsented,
        acceptAll,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
